import React from 'react';
import { Errorbox } from '../../atoms/Errorbox';
import { Text } from '../../atoms/Text';
import { List } from '../../atoms/List';
import { ListItem } from '../../atoms/ListItem';
import './Form.css';

export interface FormProps extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  /** Form content (FormFields, buttons, etc.) */
  children: React.ReactNode;
  /** Custom submit handler - receives FormData and event. If provided, prevents default. */
  onSubmit?: (data: FormData, event: React.FormEvent<HTMLFormElement>) => void | Promise<void>;
  /** Validation errors to display in summary. Keys are field names, values are messages. */
  errors?: Record<string, string | string[]>;
  /** Show error summary above form fields */
  showErrorSummary?: boolean;
  /** Form is submitting - disables all fields via fieldset */
  submitting?: boolean;
  /** Additional CSS class */
  className?: string;
}

export const Form = React.forwardRef<HTMLFormElement, FormProps>(
  (
    {
      children,
      onSubmit,
      errors,
      showErrorSummary = false,
      submitting = false,
      className = '',
      ...rest
    },
    ref
  ) => {
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      if (onSubmit) {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        onSubmit(formData, event);
      }
      // If no onSubmit, form submits normally (traditional POST)
    };

    // Collect all error messages for summary
    const errorMessages: string[] = [];
    if (errors) {
      Object.entries(errors).forEach(([, value]) => {
        if (Array.isArray(value)) {
          errorMessages.push(...value);
        } else if (value) {
          errorMessages.push(value);
        }
      });
    }

    const hasErrors = errorMessages.length > 0;

    const classes = [
      'hi-ui-form',
      submitting && 'hi-ui-form-submitting',
      hasErrors && 'hi-ui-form-has-errors',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <form ref={ref} className={classes} onSubmit={handleSubmit} {...rest}>
        {showErrorSummary && hasErrors && (
          <Errorbox className="hi-ui-form-error-summary" role="alert">
            <Text variant="body" bold>
              Please fix the following errors:
            </Text>
            <List listStyle="disc">
              {errorMessages.map((message, index) => (
                <ListItem key={index}>{message}</ListItem>
              ))}
            </List>
          </Errorbox>
        )}
        <fieldset disabled={submitting} className="hi-ui-form-fieldset">
          {children}
        </fieldset>
      </form>
    );
  }
);

Form.displayName = 'Form';
